import type { Download } from "@/payload-types";

export type DownloadOverviewSectionEntry = {
	key: string;
	title: string;
	description: string;
	items: Download[];
};

export type DownloadOverviewCategoryOption = {
	value: string;
	label: string;
};

export function normalizeDownloadSections(sections: NonNullable<unknown>): DownloadOverviewSectionEntry[] {
	if (!Array.isArray(sections)) {
		return [];
	}

	const normalized: DownloadOverviewSectionEntry[] = [];

	for (let index = 0; index < sections.length; index++) {
		const section = sections[index] as { id?: string | null; title?: string | null; description?: string | null; items?: unknown[] | null } | null;
		if (!section) {
			continue;
		}

		const title = typeof section.title === "string" ? section.title.trim() : "";
		const description = typeof section.description === "string" ? section.description.trim() : "";
		const items = (section.items ?? [])
			.filter((item): item is Download => typeof item === "object" && item !== null && !Array.isArray(item))
			.filter((item) => typeof item.file === "object" && item.file !== null);

		if (items.length === 0) {
			continue;
		}

		normalized.push({
			key: section.id ?? `section-${index}`,
			title,
			description,
			items,
		});
	}

	return normalized;
}

/**
 * Builds category filter options from normalized sections.
 *
 * @param sections - Renderable download sections.
 * @returns Combobox options for the category filter.
 */
export function buildCategoryOptions(sections: DownloadOverviewSectionEntry[]): DownloadOverviewCategoryOption[] {
	return sections
		.filter((section) => section.title.length > 0)
		.map((section) => ({
			value: section.key,
			label: section.title,
		}));
}

/**
 * Applies category and search filters to download sections.
 *
 * @param sections - Renderable download sections.
 * @param query - Fulltext query matched against download titles.
 * @param category - Selected section key, or empty for all.
 * @returns Filtered sections preserving section grouping.
 */
export function filterDownloadSections(sections: DownloadOverviewSectionEntry[], query: string, category: string): DownloadOverviewSectionEntry[] {
	const normalizedQuery = query.trim().toLowerCase();
	const scopedSections = category.length > 0 ? sections.filter((section) => section.key === category) : sections;

	return scopedSections
		.map((section) => {
			const visibleItems =
				normalizedQuery.length > 0 ? section.items.filter((item) => (item.title ?? "").toLowerCase().includes(normalizedQuery)) : section.items;

			return {
				...section,
				items: visibleItems,
			};
		})
		.filter((section) => section.items.length > 0);
}
